/**
 * @author: lv.chen
 * @date: 2025/4/3
 * @desc: 固定日期粒度的RangePicker组件的Button组件
 */
import { formatDateStr } from '@gp/utils/common';
import classnames from 'classnames';
import React, { useMemo } from 'react';

import type { IDateType, IGPRangePickerValue } from '@gp/components/RangePicker';
import type { IBaseProps } from '@home/types/common';

import './index.scss';

interface ICommonDateRangePickerButtonProps extends IBaseProps {
  name?: string;
  value: IGPRangePickerValue;
  /**
   * RangePicker组件在渲染自定义Button时，会自动注入onClick这个props方法
   * 并且一定要监听自定义Button的根节点dom的onClick事件，并且在onClick事件的回调里 调用onClick这个注入的props方法，否则点击RangePicker组件的自定义Button时，无法弹出日期选择面板
   */
  onClick?: (e: React.SyntheticEvent) => void;
}

const DATE_TYPE_NAME_MAPPING: Record<IDateType, string> = {
  date: 'Day',
  week: 'Week',
  month: 'Month',
};

const DATE_PATTERN_MAPPING: Record<IDateType, string> = {
  date: 'YYYY-MM-DD',
  week: 'YYYY-MM-DD',
  month: 'YYYY-MM',
};

const DATE_TYPE_CLASSNAME_MAPPING: Record<IDateType, string> = {
  date: 'is-date',
  week: 'is-week',
  month: 'is-month',
};

const CommonRangePickerPlaceholder = () => (
  <span className="dbg-common-date-range-picker-button__placeholder">
    Select Date ~ Select Date
  </span>
);

const CommonDateRangePickerButton: React.FC<ICommonDateRangePickerButtonProps> = (props) => {
  const { style, className, name, value, onClick, disabled = false } = props;
  const { dateType, startDate, endDate } = value || {};
  const isNoData = !dateType || !startDate || !endDate;

  const dateRangePicker = useMemo<string | React.ReactElement | null>(() => {
    if (isNoData) return <CommonRangePickerPlaceholder />;
    const pattern = DATE_PATTERN_MAPPING[dateType];
    const startDateFormated = formatDateStr(startDate, pattern);
    const endDateFormated = formatDateStr(endDate, pattern);
    return `${startDateFormated} ~ ${endDateFormated}`;
  }, [isNoData, dateType, startDate, endDate]);

  return (
    <div
      style={style}
      className={classnames(
        'dbg-common-date-range-picker-button',
        className,
        DATE_TYPE_CLASSNAME_MAPPING[dateType],
        {
          'is-no-data': isNoData,
        },
        {
          'is-disabled': disabled,
        },
      )}
      onClick={onClick}
    >
      <span className="dbg-common-date-range-picker-button__prefix">
        {name || DATE_TYPE_NAME_MAPPING[dateType]}
      </span>
      {dateRangePicker}
    </div>
  );
};

export default CommonDateRangePickerButton;
